import { IssueStatus } from '../models/database/issue.interface';

/**
 * PD-16525 — which issue statuses apply to which issues, and how each one is
 * shown.
 *
 * There is ONE `IssueStatus` enum, but two workflows use it:
 *
 *  - A support request a customer submitted through the storefront (the
 *    issue has a portal submitter, `issue.fk_public_user__id_submitter`) is a
 *    conversation. Its status says whose turn it is: Needs Reply (the
 *    customer is waiting on us), In Progress, Waiting on Customer, Resolved
 *    (we think it is fixed and asked the customer to confirm) and Done.
 *  - An issue raised inside the CRM keeps the original three: To-Do, In
 *    Progress, Done.
 *
 * `IssueService.saveIssue` enforces the subset once, server-side, so every
 * writer (the CRM screens, list inline edits, AI tools) is held to it. The
 * helpers here are the single source for both sides.
 */

/** Statuses a storefront (portal) support request may take, in workflow order. */
export const PORTAL_ISSUE_STATUSES: ReadonlyArray<IssueStatus> = [
	IssueStatus.NEEDS_REPLY,
	IssueStatus.IN_PROGRESS,
	IssueStatus.WAITING_ON_CUSTOMER,
	IssueStatus.RESOLVED,
	IssueStatus.DONE,
];

/** Statuses an issue raised inside the CRM may take. */
export const CRM_ISSUE_STATUSES: ReadonlyArray<IssueStatus> = [
	IssueStatus.TODO,
	IssueStatus.IN_PROGRESS,
	IssueStatus.DONE,
];

/**
 * Every status, in the order filters and pickers list them: the open ones
 * first, the two closed ones last.
 */
export const ALL_ISSUE_STATUSES: ReadonlyArray<IssueStatus> = [
	IssueStatus.TODO,
	IssueStatus.NEEDS_REPLY,
	IssueStatus.IN_PROGRESS,
	IssueStatus.WAITING_ON_CUSTOMER,
	IssueStatus.RESOLVED,
	IssueStatus.DONE,
];

/**
 * Statuses that still need someone to do something. Resolved is NOT open:
 * the work is finished and only the customer's confirmation is outstanding
 * (the nightly auto-close finishes it if they never answer).
 */
export const OPEN_ISSUE_STATUSES: ReadonlyArray<IssueStatus> = [
	IssueStatus.TODO,
	IssueStatus.NEEDS_REPLY,
	IssueStatus.IN_PROGRESS,
	IssueStatus.WAITING_ON_CUSTOMER,
];

/**
 * The status a staff reply the customer can see sets by default: the ball is
 * now in the customer's court. The CRM composer presets its "Send as" choice
 * to this and lets the sender pick another portal status.
 */
export const DEFAULT_STAFF_REPLY_ISSUE_STATUS = IssueStatus.WAITING_ON_CUSTOMER;

/**
 * Whether an issue is a storefront support request. Accepts either the
 * submitter id (list rows, entities) or the hydrated submitter (issue detail).
 */
export function isPortalIssue(
	issue:
		| {
				submitterPublicUserId?: number | null;
				submitterPublicUser?: object | null;
		  }
		| null
		| undefined,
): boolean {
	if (issue == null) {
		return false;
	}

	return (
		issue.submitterPublicUserId != null || issue.submitterPublicUser != null
	);
}

/** The statuses an issue of this origin may take. */
export function allowedIssueStatuses(
	isPortal: boolean,
): ReadonlyArray<IssueStatus> {
	return isPortal ? PORTAL_ISSUE_STATUSES : CRM_ISSUE_STATUSES;
}

/** True when `status` is one of the statuses an issue of this origin may take. */
export function isIssueStatusAllowed(
	status: string | null | undefined,
	isPortal: boolean,
): boolean {
	if (status == null) {
		return false;
	}

	return (allowedIssueStatuses(isPortal) as ReadonlyArray<string>).includes(
		status,
	);
}

/** True for a finished issue: Resolved (awaiting confirmation) or Done. */
export function isIssueClosed(status: string | null | undefined): boolean {
	return status === IssueStatus.RESOLVED || status === IssueStatus.DONE;
}

/**
 * The badge treatment for a status in the CRM. The names are a subset of the
 * web-ui `BadgeVariant` (and of its `badge*` class tokens), kept here as plain
 * strings because this library has no React dependency.
 */
export type IssueStatusBadgeVariant =
	| 'info'
	| 'warning'
	| 'success'
	| 'danger'
	| 'default';

/**
 * The one status-to-color rule for the CRM (badges, selectors, the menu of a
 * status picker). Needs Reply is the only status that means a customer is
 * waiting on the team, so it is the one that draws the eye.
 */
export function issueStatusBadgeVariant(
	status: string | null | undefined,
): IssueStatusBadgeVariant {
	switch (status) {
		case IssueStatus.TODO:
			return 'info';
		case IssueStatus.NEEDS_REPLY:
			return 'danger';
		case IssueStatus.IN_PROGRESS:
			return 'warning';
		case IssueStatus.RESOLVED:
		case IssueStatus.DONE:
			return 'success';
		case IssueStatus.WAITING_ON_CUSTOMER:
		default:
			return 'default';
	}
}

/**
 * The words a customer reads for a status on the storefront. Written from the
 * customer's side of the conversation: our "Needs Reply" is their "Open", our
 * "Waiting on Customer" is their "Awaiting your reply". A legacy To-Do (an
 * issue created before PD-16525) also reads "Open". An unknown status is shown
 * as it is.
 */
export function portalIssueStatusLabel(status: string): string {
	switch (status as IssueStatus) {
		case IssueStatus.NEEDS_REPLY:
		case IssueStatus.TODO:
			return 'Open';
		case IssueStatus.IN_PROGRESS:
			return 'In Progress';
		case IssueStatus.WAITING_ON_CUSTOMER:
			return 'Awaiting your reply';
		case IssueStatus.RESOLVED:
			return 'Resolved';
		case IssueStatus.DONE:
			return 'Closed';
		default:
			return status;
	}
}

/** One option of the storefront's Status filter and the statuses it covers. */
export interface PortalIssueStatusFilterModel {
	/** The `status` URL parameter value. */
	key: string;
	/** Customer-facing label (passed through `t()` by the storefront). */
	label: string;
	statuses: ReadonlyArray<IssueStatus>;
}

/**
 * The storefront Support page's Status filter. Each option is a group of
 * statuses that share a customer-facing label, so "Open" also finds a
 * legacy To-Do request.
 */
export const PORTAL_ISSUE_STATUS_FILTERS: ReadonlyArray<PortalIssueStatusFilterModel> =
	[
		{
			key: 'open',
			label: 'Open',
			statuses: [IssueStatus.NEEDS_REPLY, IssueStatus.TODO],
		},
		{
			key: 'in-progress',
			label: 'In Progress',
			statuses: [IssueStatus.IN_PROGRESS],
		},
		{
			key: 'awaiting-reply',
			label: 'Awaiting your reply',
			statuses: [IssueStatus.WAITING_ON_CUSTOMER],
		},
		{
			key: 'resolved',
			label: 'Resolved',
			statuses: [IssueStatus.RESOLVED],
		},
		{
			key: 'closed',
			label: 'Closed',
			statuses: [IssueStatus.DONE],
		},
	];

/**
 * The statuses a storefront Status filter key stands for, or null when the
 * key is empty or unknown (which the page treats as "all").
 */
export function portalIssueStatusFilterStatuses(
	key: string | null | undefined,
): ReadonlyArray<IssueStatus> | null {
	if (key == null || key === '') {
		return null;
	}

	return (
		PORTAL_ISSUE_STATUS_FILTERS.find((filter) => filter.key === key)
			?.statuses ?? null
	);
}
